import React, { useState, useEffect } from "react";
import API_BASE_URL from "../../../../Constants";

import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import ViewDeviceModal from "./DeviceInspections/ViewDeviceModal";
import ViolationsModal from "./DeviceVolatingConditions/ViolationsEntryModal";
import InspectionDeviceEditModal from "./InspectionDeviceEdit/InspectionDeviceEditModal";

/**
 * Utility: Format a date string (ISO or otherwise) into "YYYY-MM-DDTHH:mm"
 * for an <input type="datetime-local" /> value.
 */
function formatForDateTimeLocal(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  // toISOString() => "2025-04-10T12:34:56.789Z"
  // slice(0,16)   => "2025-04-10T12:34"
  return date.toISOString().slice(0, 16);
}

const DeviceListTable = ({
  inspectionId,
  buildingSite = "",
  agencyId = null,
  categoryInspection = "",
  inspYear = "",
  inspectionDate = "",
  scheduledDate = "",
  inspectionStatus = "",
  inspectionData = {},
}) => {
  const [assignedDevices, setAssignedDevices] = useState([]);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [openViolationsModal, setOpenViolationsModal] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState("");
  const [selectedInspectionId, setSelectedInspectionId] = useState(null);

  const [openInspectionModal, setOpenInspectionModal] = useState(false);
  const [modalDevices, setModalDevices] = useState([]);
  const [modalDeviceIndex, setModalDeviceIndex] = useState(null);
  const [deviceEditData, setDeviceEditData] = useState({});

  useEffect(() => {
    if (!inspectionId) {
      setAssignedDevices([]);
      return;
    }

    async function fetchDevices() {
      try {
        // 1) GET all devices for this inspection
        const url = `${API_BASE_URL}/api/inspections/id/${inspectionId}/devices`;
        const res = await fetch(url, {
          // REMOVE credentials: "include"
          method: "GET",
        });
        if (!res.ok) {
          if (res.status === 401) {
            // Optional: handle 401 ourselves
            console.error("Unauthorized; custom handling here");
            return;
          }
          throw new Error(`Failed. Status => ${res.status}`);
        }
        let rawList = await res.json();
        if (!Array.isArray(rawList)) rawList = [];

        // 2) For each device, fetch bridging info & violation count
        const enriched = await Promise.all(
          rawList.map(async (dev) => {
            // (A) bridging record
            try {
              const bridgingUrl = `${API_BASE_URL}/api/inspection-devices/${inspectionId}/${dev.deviceId}`;
              const bridgingRes = await fetch(bridgingUrl, {
                method: "GET",
              });
              if (bridgingRes.ok) {
                const bridgingData = await bridgingRes.json();
                dev.status = bridgingData.status ?? "N/A";
                dev.servicerId = bridgingData.servicerId ?? bridgingData.servicer ?? null;

                if (dev.servicerId) {
                  try {
                    // fetch the inspector name
                    const inspUrl = `${API_BASE_URL}/api/inspectors/${dev.servicerId}`;
                    const inspRes = await fetch(inspUrl, { method: "GET" });
                    if (inspRes.ok) {
                      const inspObj = await inspRes.json();
                      dev.inspectedBy = inspObj.inspectorName || `Inspector #${dev.servicerId}`;
                    } else {
                      dev.inspectedBy = `ID #${dev.servicerId} (not found)`;
                    }
                  } catch (e) {
                    dev.inspectedBy = `ID #${dev.servicerId} (error)`;
                  }
                } else {
                  dev.inspectedBy = "N/A";
                }
              } else {
                dev.status = "N/A";
                dev.servicerId = null;
                dev.inspectedBy = "N/A";
              }
            } catch (err) {
              dev.status = "N/A";
              dev.servicerId = null;
              dev.inspectedBy = "N/A";
            }

            // (B) violation count
            try {
              const violUrl = `${API_BASE_URL}/api/violations/count/inspection/${inspectionId}/device/${dev.deviceId}`;
              const violRes = await fetch(violUrl, { method: "GET" });
              if (violRes.ok) {
                const count = await violRes.json();
                dev.violationCount = count;
              } else {
                dev.violationCount = 0;
              }
            } catch (err) {
              dev.violationCount = 0;
            }

            return dev;
          })
        );

        setAssignedDevices(enriched);
      } catch (err) {
        console.error("Error =>", err);
        setAssignedDevices([]);
      }
    }
    fetchDevices();
  }, [inspectionId]);

  // Inline editing handlers
  const handleEditClick = (index) => {
    setAssignedDevices((prev) =>
      prev.map((device, i) => (i === index ? { ...device, isEditing: true } : device))
    );
  };
  const handleCancelClick = (index) => {
    setAssignedDevices((prev) =>
      prev.map((device, i) => (i === index ? { ...device, isEditing: false } : device))
    );
  };
  const handleChange = (e, index) => {
    const { name, value, type, checked } = e.target;
    const finalVal = type === "checkbox" ? checked : value;
    setAssignedDevices((prev) =>
      prev.map((dev, i) => (i === index ? { ...dev, [name]: finalVal } : dev))
    );
  };
  const onUpdateDevice = async (devId, updatedDevice) => {
    alert(`Updated bridging for device #${devId}.`);
    setAssignedDevices((prev) =>
      prev.map((d) => (d.deviceId === devId ? { ...updatedDevice, isEditing: false } : d))
    );
  };

  // View device info
  const handleViewClick = (index) => {
    setCurrentIndex(index);
    setOpenViewModal(true);
  };
  const handleCloseModal = () => setOpenViewModal(false);

  // Violations
  const handleOpenViolations = (device) => {
    setSelectedDeviceId(device.deviceId);
    setSelectedDeviceName(`Device #${device.deviceId}`);
    setSelectedInspectionId(inspectionId);
    setOpenViolationsModal(true);
  };
  const handleCloseViolationsModal = () => {
    setOpenViolationsModal(false);
  };

  // Steps editing (InspectionDeviceEditModal)
  const handleOpenDeviceSteps = (device, index) => {
    setDeviceEditData({ deviceId: device.deviceId });
    setModalDevices(assignedDevices);
    setModalDeviceIndex(index);
    setOpenInspectionModal(true);
  };
  const handleCloseDeviceSteps = () => setOpenInspectionModal(false);
  const handleSaveDeviceSteps = (updatedData) => {
    alert("Steps saved");
    // Possibly refresh or update assignedDevices here if needed
  };

  if (!assignedDevices.length) {
    return (
      <div>
        <h3 className="text-xl font-bold mt-6">Device Details</h3>
        <div className="mt-4 text-center">
          No devices found for Inspection #{inspectionId}.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl font-bold mt-6">Device Details</h3>
      <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg border-2 border-gray-300 mt-2">
        <thead className="bg-gray-200">
          <tr>
            <th>Edit</th>
            <th>Device</th>
            <th>Device ID</th>
            <th>Current Step</th>
            <th>Inspection Date</th>
            <th>Inspector</th>
            <th>Total</th>
            <th>Violations</th>
            <th>Step</th>
          </tr>
        </thead>
        <tbody className="text-gray-800">
          {assignedDevices.map((device, index) => (
            <tr key={device.deviceId || index} className="border-t hover:bg-gray-100">
              {/* Edit / Save / Cancel buttons */}
              <td className="p-1 border bg-white text-center">
                {device.isEditing ? (
                  <>
                    <button
                      onClick={() => onUpdateDevice(device.deviceId, device)}
                      className="text-green-500 hover:bg-gray-300 rounded-full mr-1"
                      title="Save Device"
                    >
                      <CheckIcon />
                    </button>
                    <button
                      onClick={() => handleCancelClick(index)}
                      className="text-red-500 hover:bg-gray-300 rounded-full"
                      title="Cancel Edit"
                    >
                      <CloseIcon />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleEditClick(index)}
                    className="border-gray-200 hover:bg-gray-100 rounded-xl transition ease-in-out border-2 p-2"
                    title="Edit Device"
                  >
                    <EditIcon />
                  </button>
                )}
              </td>

              {/* Device Tests */}
              <td className="px-4 py-2 border bg-white">
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-md px-2 py-1"
                  onClick={() => handleViewClick(index)}
                >
                  View
                </button>
              </td>

              {/* Device ID */}
              <td className="px-4 py-2 border bg-white">
                {device.deviceId ?? "N/A"}
              </td>

              {/* Current Step (inline-editing) */}
              <td className="px-4 py-2 border bg-white">
                {device.isEditing ? (
                  <input
                    type="text"
                    name="status"
                    value={device.status || ""}
                    onChange={(e) => handleChange(e, index)}
                    className="border border-gray-300 rounded p-1 w-full"
                  />
                ) : (
                  device.status || "N/A"
                )}
              </td>

              {/* Inspection Date (display date/time if not editing) */}
              <td className="px-4 py-2 border bg-white">
                {device.isEditing ? (
                  // Use datetime-local for both date & time
                  <input
                    type="datetime-local"
                    name="inspectionDate"
                    value={formatForDateTimeLocal(device.inspectionDate || inspectionDate)}
                    onChange={(e) => handleChange(e, index)}
                    className="w-full border border-gray-300 rounded-md p-1"
                  />
                ) : device.inspectionDate ? (
                  new Date(device.inspectionDate).toLocaleString()
                ) : inspectionDate ? (
                  new Date(inspectionDate).toLocaleString()
                ) : (
                  "N/A"
                )}
              </td>

              {/* Inspector */}
              <td className="px-4 py-2 border bg-white">
                {device.isEditing ? (
                  <input
                    name="inspectedBy"
                    value={device.inspectedBy || ""}
                    onChange={(e) => handleChange(e, index)}
                    className="w-full border border-gray-300 rounded-md p-1"
                  />
                ) : (
                  device.inspectedBy || "N/A"
                )}
              </td>

              {/* Violations count */}
              <td className="px-4 py-2 border bg-white text-center">
                {device.violationCount ?? 0}
              </td>

              {/* View Violations button */}
              <td className="px-4 py-2 border bg-white text-center">
                <button
                  onClick={() => handleOpenViolations(device)}
                  className="text-blue-600 hover:text-blue-900"
                >
                  <VisibilityIcon />
                </button>
              </td>

              {/* Steps button */}
              <td className="px-4 py-2 border bg-white">
                <button
                  onClick={() => handleOpenDeviceSteps(device, index)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* View Device Modal */}
      {openViewModal && (
        <ViewDeviceModal
          open={openViewModal}
          onClose={handleCloseModal}
          devices={assignedDevices}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          buildingSite={buildingSite}
          agencyId={agencyId}
          setDevices={setAssignedDevices}
          refreshDevices={() => console.log("Refresh")}
        />
      )}

      {/* Violations Modal */}
      {openViolationsModal && (
        <ViolationsModal
          open={openViolationsModal}
          onClose={handleCloseViolationsModal}
          deviceId={selectedDeviceId}
          deviceName={selectedDeviceName}
          inspectionNumber={selectedInspectionId}
          buildingName={buildingSite}
          tableData={[]}
        />
      )}

      {/* Inspection Device Steps Modal */}
      {openInspectionModal && (
        <InspectionDeviceEditModal
          open={openInspectionModal}
          onClose={handleCloseDeviceSteps}
          inspectionNumber={inspectionId}
          deviceId={deviceEditData.deviceId}
          onSave={handleSaveDeviceSteps}
          categoryInspection={categoryInspection}
          buildingSite={buildingSite}
          agencyId={agencyId}
          inspYear={inspYear}
          inspectionStatus={inspectionStatus}
          scheduledDate={scheduledDate}
          inspectionDate={inspectionDate}
          onOpenViolationsModal={() => {}}
          inspectionData={inspectionData || {}}
          devices={modalDevices}
          deviceIndex={modalDeviceIndex}
          violationNumber={deviceEditData.violationNumber || ""}
          initialData={{}}
        />
      )}
    </div>
  );
};

export default DeviceListTable;
